import { FormErrors } from '../modules/FormErrors';
import {
    executeRecaptcha,
    preloadRecaptchaOnInteraction,
} from '../modules/recaptcha-loader';

class NewsletterForm {
    constructor(root) {
        this.root = root;
        if (!this.root) return;

        this.init();
    }

    init() {
        this.root.setAttribute('novalidate', '');
        this.parent = this.root.parentElement;
        this.loader = this.root.querySelector('.loader');
        this.requiredCheckboxes = this.root.querySelectorAll(
            'input[type="checkbox"][required]'
        );

        this.formErrors = new FormErrors(this.root);
        this.recaptchaKey = this.root.querySelector(
            '[name="g_recaptcha_key"]'
        )?.value;

        if (this.recaptchaKey) {
            preloadRecaptchaOnInteraction(this.root, this.recaptchaKey);
        }

        this.newsletterOutput = this.parent.querySelector(
            '[js-newsletter-output]'
        );
        this.newsletterOutputIcon = this.newsletterOutput?.querySelector(
            '[js-newsletter-output-icon]'
        );
        this.newsletterOutputHeading = this.newsletterOutput?.querySelector(
            '[js-newsletter-output-heading]'
        );
        this.newsletterOutputDescription = this.newsletterOutput?.querySelector(
            '[js-newsletter-output-description]'
        );

        this.events();
    }

    events() {
        this.root.addEventListener('submit', this.onFormSubmit.bind(this));
        this.root.addEventListener(
            'invalid',
            this.onFieldInvalid.bind(this),
            true
        );

        if (this.requiredCheckboxes.length) {
            this.requiredCheckboxes.forEach(checkbox => {
                checkbox.addEventListener(
                    'change',
                    this.onRequiredCheckboxChange.bind(this)
                );
            });
        }
    }

    onRequiredCheckboxChange(e) {
        const checkbox = e.target;

        if (checkbox.checked) {
            this.formErrors.handleFieldError(checkbox, false);
        }
    }

    onFieldInvalid(e) {
        e.preventDefault();
    }

    validateFields() {
        let isValid = true;
        const nameField = this.root.querySelector('[name="name"]');
        const emailField = this.root.querySelector('[name="email"]');

        if (nameField) {
            const name = nameField.value.trim();

            if (name === '') {
                isValid = false;
                this.formErrors.handleFieldError(
                    nameField,
                    true,
                    phrases.validation.empty
                );
            } else {
                this.formErrors.handleFieldError(nameField, false);
            }
        }

        if (emailField) {
            const email = emailField.value.trim();

            if (email === '') {
                isValid = false;
                this.formErrors.handleFieldError(
                    emailField,
                    true,
                    phrases.validation.empty
                );
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                isValid = false;
                this.formErrors.handleFieldError(
                    emailField,
                    true,
                    phrases.validation.invalidEmail
                );
            } else {
                this.formErrors.handleFieldError(emailField, false);
            }
        }

        return isValid;
    }

    validateRequiredCheckboxes() {
        let isValid = true;

        if (!this.requiredCheckboxes.length) return isValid;

        this.requiredCheckboxes.forEach(checkbox => {
            if (checkbox.checked) {
                this.formErrors.handleFieldError(checkbox, false);
                return;
            }

            isValid = false;
            this.formErrors.handleFieldError(
                checkbox,
                true,
                phrases.validation.emptyCheckbox
            );
        });

        return isValid;
    }

    async onFormSubmit(e) {
        e.preventDefault();
        try {
            const formData = new FormData(this.root);
            this.formErrors.clearErrors();

            if (!this.validateFields() || !this.validateRequiredCheckboxes()) {
                return;
            }

            this.loader.classList.add('active');

            const token = await executeRecaptcha(this.recaptchaKey, 'submit');
            formData.append('g_recaptcha_token', token);
            formData.append('referer_site', window.location.href);

            const req = await fetch(ajax.url, {
                method: 'POST',
                body: formData,
            });

            const res = await req.json();
            const { data, success } = res;

            if (!success && data.fields) {
                const { fields } = data;
                for (const [key, value] of Object.entries(fields)) {
                    this.formErrors.handleFieldError(key, true, value);
                }
            } else {
                res.data.status = success ? 'success' : 'error';
                this.handleFormOutput(res.data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.loader.classList.remove('active');
        }
    }

    handleFormOutput(data) {
        this.root.classList.add('hidden');
        this.newsletterOutput.classList.add(`-${data.status}`);
        this.newsletterOutputIcon.classList.remove('icon-check', 'icon-close');
        const isSuccess = data.status === 'success';
        this.newsletterOutputIcon.classList.add(
            isSuccess ? 'icon-check' : 'icon-close'
        );

        this.newsletterOutputHeading.textContent = data.heading
            ? data.heading
            : isSuccess
            ? phrases.newsletter.heading.success
            : phrases.newsletter.heading.error;
        this.newsletterOutputDescription.textContent = data.message ?? '';
    }
}

const elements = document.querySelectorAll('[js-newsletter-form]');

if (elements.length) {
    elements.forEach(element => {
        new NewsletterForm(element);
    });
}
